import React, { useContext, useMemo } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns as AdapterDateFnsV2 } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { he, enUS } from 'date-fns/locale';
import { useAppSelector } from 'store';
import { selectLanguageIsRtl } from 'store/languages';

type Locale = typeof he | typeof enUS;
type Value = {
  locale: Locale;
};
const ContextLocale = React.createContext<Value | null>(null);

export const useDateLocale = () => {
  const context = useContext(ContextLocale);
  if (!context) {
    throw new Error('useDateLocale: Context error');
  }

  return context;
};

/**
 * Date-picker localisation at the app root (Phase D1: @material-ui/pickers 3
 * -> @mui/x-date-pickers). `AdapterDateFnsV2` is the adapter for the date-fns 2
 * already in the tree; the locale follows the UI language exactly as the
 * `MuiPickersUtilsProvider` did.
 */
export const PickerProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const isRtl = useAppSelector(selectLanguageIsRtl);
  const locale = isRtl ? he : enUS;

  const value = useMemo(() => ({ locale }), [locale]);
  return (
    <LocalizationProvider dateAdapter={AdapterDateFnsV2} adapterLocale={locale}>
      <ContextLocale.Provider value={value}>{children}</ContextLocale.Provider>
    </LocalizationProvider>
  );
};
